import DefaultLayout from '../../components/DefaultLayout';
import { Form } from '../../../ui/Form/Form';
import { DefaultThirdHeader } from '../../components/DefaultLayout/DefaultThirdHeader';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { Button, Icon, Spinner } from '../../../ui';
import { message, Pagination } from 'antd';
import { Card } from '../../../ui/Card';
import { ProductData } from '../../../types/ProductData';
import { useCallback, useEffect, useState } from 'react';
import { StyledAllProducts } from './style';
import { ProductsTopFilter } from '../../../ui/Form/filters/ProductsTopFilter';
import { exportIds, options } from '../../../config/config';
import MobileProductCategoriesCarousel from 'public/components/MobileLayout/MobileProductCategoriesCarousel/MobileProductCategoriesCarousel';
import {
  useBannerState,
  useCategoryState,
  useProductState,
} from 'public/context';
import Banner from 'public/components/DefaultLayout/Banner';
import { MobileMenuDrawer } from 'ui/Drawer/Drawers/MobileMenuDrawer';
import { StyledMobileFilterFooter } from 'public/components/MobileLayout/MobileMenu/MobileFilterFooter/style';
import MobileFilter from 'public/components/MobileLayout/MobileFilter/MobileFilter';
import { Filter } from 'types/Filter';
import { FilterOptions } from 'types/FilterOptions';
import SearchAndFilter from 'public/components/DefaultLayout/SearchAndFilter';
import {
  useFilterDispatch,
  useFilterState,
} from 'public/context/FilterContext';
import { useLocation, useNavigate } from 'react-router-dom';
import PopularCategories from 'public/components/DefaultLayout/PopularCategories';
import DiscountItems from 'public/components/DefaultLayout/DiscountItems/DiscountItems';
import { paginationNavigationRenderer } from 'utils/paginationNavigationrenderer';
import { useCategoryDispatch } from '../../../public/context';
import useMarketType from '../../../utils/useMarketType';
import PublicRoute from 'enums/publicRoutes';
import useSearchParamsHelper from '../../../utils/useSearchParamsHelper';
import useScroll from 'utils/useScroll';
import {
  buildMapNavigationParams,
  buildFilterChangeParams,
} from '../../../utils/filterHelper';
import { useListPageUrlSync } from '../../../utils/useListPageUrlSync';
import { buildPath, useListPagination } from '../../../utils/listPagination';
import { captureElementToPdf } from '../../../ui/ProductExport/exportProductListPdf';
import { ExportPdfWrapper } from '../../../ui/ProductExport/ExportPdfWrapper';
import { unmountExportRoot } from '../../../ui/ProductExport/exportRoot';
import { useClearFilterHandlers } from '../../../utils/useClearFilterHandlers';

const DEFAULT_IMAGE = options.images.defaultProductImage;

export const AllProducts = () => {
  const location = useLocation();
  const navigation = useNavigate();
  const [filter, setFilter] = useState<Filter>({});
  const [total, setTotal] = useState<number>(0);
  const [products, setProducts] = useState<ProductData[]>([]);
  const { popularCategory, activeCategory } = useCategoryState();
  const { productsDiscounted } = useProductState();
  const { banners } = useBannerState();
  const banner_3_data = banners.data.find(
    (banner) => banner.type === 'banner_3'
  );
  const banner_4_data = banners.data.find(
    (banner) => banner.type === 'banner_4'
  );
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});

  const {
    page: currentPage,
    pageSize,
    setPage,
    setPageSize,
  } = useListPagination();
  const { marketType, isEmarket } = useMarketType();

  const dispatchFilter = useFilterDispatch();
  const { isOpenMobileFilter } = useFilterState();
  const { getProperFilterValues } = useSearchParamsHelper();
  const { scrollToTop } = useScroll();
  const [prevSearch, setPrevSearch] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [showExportOverlay, setShowExportOverlay] = useState(false);

  const openCloseDrawer = useCallback(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isOpenMobileFilter: !isOpenMobileFilter },
    });
    addClassToHeader();
  }, [isOpenMobileFilter]);

  const dispatch = useCategoryDispatch();

  useEffect(() => {
    dispatch({
      type: 'SAVE_ACTIVE_CATEGORY',
      activeCategory: null,
    });
  }, []);

  useEffect(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isFilter: true, openCloseDrawer },
    });
  }, [dispatchFilter, openCloseDrawer]);

  const addClassToHeader = () => {
    const header = document.getElementById('mobile-header-container');

    if (header) {
      if (!isOpenMobileFilter) {
        header.classList.add('filterOpen');
      } else {
        header.classList.remove('filterOpen');
      }
    }
  };

  const { isLoading, appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/products',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setTotal(response.products.total);
      setProducts(response.products.data);
      setFilterOptions(response.filters);
    },
  });

  useListPageUrlSync({
    setFilter,
    setPrevSearch,
    prevSearch,
    scrollToTop,
    getProperFilterValues,
    filterFields: ['qmark', 'delivery', 'services', 'farm', 'cooperative'],
    onSync: ({ filterParams, page, page_size, currentSearch }) => {
      appendData({
        filter: { ...filterParams, type: marketType },
        page,
        page_size,
        fullSearch: currentSearch,
      });
    },
  });

  const handleFilterChange = (newFilter: Filter) => {
    const oldParams = new URLSearchParams(location.search);
    const params = buildFilterChangeParams({
      filter: newFilter,
      search: oldParams.get('search') || undefined,
      pageSize,
    });
    navigation(buildPath(location.pathname, params), {
      replace: true,
    });

    if (isOpenMobileFilter) {
      openCloseDrawer();
    }
  };

  const { clearAppliedFilters, clearDraftFilters } = useClearFilterHandlers({
    filter,
    setFilter,
    onFilterChange: handleFilterChange,
    preserveOrdering: true,
  });

  const handleShowMap = () => {
    const params = buildMapNavigationParams({
      dataType: 'products',
      page: currentPage,
      pageSize,
      filter,
      search: new URLSearchParams(location.search).get('search') || undefined,
    });
    navigation(
      `${isEmarket ? PublicRoute.MAP : PublicRoute.WHOLESALE_MAP}?${params.toString()}`,
      {
        state: { returnPath: PublicRoute.PRODUCTS_ALL },
      }
    );
  };

  const intl = useIntl();

  const pageSizeOptions = options.filters.pageSizeOptions;

  const breadcrumbs = [
    {
      route: '/',
      name: intl.formatMessage({ id: 'general.start' }),
    },
    {
      route: 'all',
      name: intl.formatMessage({ id: 'general.products' }),
    },
  ];

  const handleExportReady = () => {
    const el =
      document.getElementById(exportIds.pdfExportContent) ??
      document.getElementById(exportIds.pdfExportRoot);
    if (!el || !el.firstElementChild) {
      message.destroy(
        intl.formatMessage({ id: 'products.export_message_key' })
      );
      unmountExportRoot();
      setShowExportOverlay(false);
      setIsExporting(false);
      return;
    }
    const filename = `produktu-saraksts-${new Date().toISOString().slice(0, 10)}.pdf`;
    captureElementToPdf(el, filename)
      .then(() => {
        message.destroy(
          intl.formatMessage({ id: 'products.export_message_key' })
        );
        message.success(intl.formatMessage({ id: 'products.pdf_downloaded' }));
      })
      .catch(() => {
        message.destroy(
          intl.formatMessage({ id: 'products.export_message_key' })
        );
        message.error(intl.formatMessage({ id: 'products.export_error' }));
      })
      .finally(() => {
        unmountExportRoot();
        setShowExportOverlay(false);
        setIsExporting(false);
      });
  };

  const handleExport = () => {
    if (marketType !== 'WHOLESALE' || isExporting) return;
    setIsExporting(true);
    message.loading({
      content: intl.formatMessage({ id: 'products.exporting_pdf' }),
      duration: 0,
      key: intl.formatMessage({ id: 'products.export_message_key' }),
    });
    setShowExportOverlay(true);
  };

  const drawer = () => {
    return (
      <MobileMenuDrawer
        title={intl.formatMessage({ id: 'product.filter' })}
        onClose={openCloseDrawer}
        open={isOpenMobileFilter}
        className="mobile-filter product-mobile-filter"
        closable={false}
        footer={
          <StyledMobileFilterFooter>
            <Button
              label={intl.formatMessage({ id: 'general.choose' })}
              icon={<Icon name={'mobile-filter-submit-arrow-icon'} />}
              onClick={() => handleFilterChange(filter)}
            />
          </StyledMobileFilterFooter>
        }
      >
        <MobileFilter
          setFilter={setFilter}
          filter={filter}
          filterOptions={filterOptions}
          onClearFilters={clearDraftFilters}
          onExport={marketType === 'WHOLESALE' ? handleExport : undefined}
          exportLoading={isExporting}
        />
      </MobileMenuDrawer>
    );
  };

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent className={'grey-background'}>
        <StyledAllProducts>
          <DefaultThirdHeader
            label={intl.formatMessage({ id: 'form.products_page_title' })}
            filters={
              <ProductsTopFilter
                total={total}
                per_page={pageSize}
                setFilter={handleFilterChange}
                page_size_options={pageSizeOptions}
                setPageSize={setPageSize}
                filters={filter}
                handleShowMap={handleShowMap}
                unit={'products'}
              />
            }
            breadcrumb={breadcrumbs}
          />
          <SearchAndFilter rootClassName="laptop-search-section-box" />
          <div className="container products-container">
            <div className={'products-filters-container'}>
              <Form
                setFilter={handleFilterChange}
                filterOptions={filterOptions}
                filter={filter}
                onClearFilters={clearAppliedFilters}
                onExport={marketType === 'WHOLESALE' ? handleExport : undefined}
                exportLoading={isExporting}
              />
              <Spinner spinning={isLoading}>
                <div className={'products-wrapper'}>
                  <p className="category_name">
                    {activeCategory
                      ? activeCategory.name
                      : intl.formatMessage({ id: 'form.products_page_title' })}
                  </p>
                  <div className={'products'}>
                    {products.map((product: ProductData, index) => (
                      <Card
                        key={`crd-${index}`}
                        product={product}
                        imageUrl={
                          product?.image_small
                            ? product?.image_small
                            : product?.farm?.logo
                              ? product?.farm?.logo
                              : DEFAULT_IMAGE
                        }
                        hoverable
                      />
                    ))}
                  </div>

                  <div className={'pagination'}>
                    {total > pageSize && (
                      <Pagination
                        total={total}
                        showSizeChanger={false}
                        current={currentPage}
                        pageSize={pageSize}
                        onChange={(page) => setPage(page)}
                        itemRender={paginationNavigationRenderer}
                      />
                    )}
                  </div>
                </div>
              </Spinner>
            </div>
          </div>
          {isEmarket && <PopularCategories categories={popularCategory} />}
          <MobileProductCategoriesCarousel popularCategory={popularCategory} />
          <div className="discount-items">
            <DiscountItems products={productsDiscounted} slidesPerRow={1} />
          </div>
          <div className="banners">
            <div className="container">
              {banner_3_data && (
                <Banner banners={[banner_3_data]} className="banner_3" />
              )}
              {banner_4_data && <Banner banners={[banner_4_data]} />}
            </div>
          </div>
        </StyledAllProducts>
        {drawer()}
        {showExportOverlay && (
          <ExportPdfWrapper
            currentPathname={location.pathname}
            currentSearch={location.search}
            totalProducts={total}
            onReady={handleExportReady}
          />
        )}
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};
